// src/components/BracketAccordion.tsx - Updated for 7-round system with Round of 64 editable
import React, { useState, useEffect, useRef } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { useConfig } from '../context/ConfigContext';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from './ui/accordion';
import { Input } from './ui/input';
import { Button } from './ui/button';

interface BracketAccordionProps {
  title: string;
  roundIndex: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
  onToggle?: (isOpen: boolean) => void;
  showEditButton?: boolean; // Add this prop
}

/**
 * Accordion component with inline editing for round names
 * Now supports editing Round of 64 (index 0) through Champion (index 6)
 */
const BracketAccordion: React.FC<BracketAccordionProps> = ({
  title,
  roundIndex,
  defaultOpen = true,
  children,
  onToggle,
  showEditButton = true, // Default to true for backward compatibility
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track the accordion value for controlled open/close
  const [accordionValue, setAccordionValue] = useState<string>(
    defaultOpen ? `round-${roundIndex}` : ''
  );

  // Get config context
  const { updateRoundName } = useConfig();

  // Derive whether the accordion is open from the value
  const isOpen = accordionValue === `round-${roundIndex}`;

  // Update edited title when title prop changes
  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync with defaultOpen prop changes
  useEffect(() => {
    setAccordionValue(defaultOpen ? `round-${roundIndex}` : '');
  }, [defaultOpen, roundIndex]);

  // Handle accordion value change
  const handleValueChange = (value: string) => {
    // Don't toggle if we're editing
    if (isEditing) return;

    setAccordionValue(value);

    if (onToggle) {
      onToggle(value === `round-${roundIndex}`);
    }
  };

  // Start editing
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    e.preventDefault();
    setIsEditing(true);
    setEditedTitle(title);
  };

  // Save changes
  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!editedTitle.trim()) {
      handleCancel(e);
      return;
    }

    setIsSaving(true);

    try {
      if (roundIndex === 0) {
        // Round of 64 is not configurable via API - only update local state
        console.warn('Round of 64 name changes are not saved to server');
        setIsEditing(false);
      } else {
        // For rounds 1-4 (Round of 32 through Elite 8), map to config indices 0-3
        await updateRoundName(roundIndex - 1, editedTitle.trim());
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving round name:', error);
      setEditedTitle(title);
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditedTitle(title);
    setIsEditing(false);
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value);
  };

  // Handle input key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave(e as any);
    } else if (e.key === 'Escape') {
      handleCancel(e as any);
    }
  };

  return (
    <Accordion
      type="single"
      collapsible
      value={accordionValue}
      onValueChange={handleValueChange}
      className="mb-4"
    >
      <AccordionItem
        value={`round-${roundIndex}`}
        className="rounded-lg border bg-card overflow-hidden"
      >
        <AccordionTrigger
          className={cn(
            'px-4 py-3 font-bold text-foreground hover:no-underline bg-muted/50',
            isOpen && 'border-b'
          )}
        >
          <div className="flex items-center gap-2 flex-1">
            {isEditing ? (
              <Input
                ref={inputRef}
                type="text"
                value={editedTitle}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                disabled={isSaving}
                className="h-7 min-w-[150px] max-w-[250px] text-sm font-bold"
              />
            ) : (
              <span>{title}</span>
            )}

            {isEditing ? (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-success hover:text-success"
                  onClick={handleSave}
                  disabled={isSaving}
                  title="Save"
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive hover:text-destructive"
                  onClick={handleCancel}
                  disabled={isSaving}
                  title="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              showEditButton && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={handleEditClick}
                  title="Edit round name"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )
            )}
          </div>
        </AccordionTrigger>

        <AccordionContent className="p-4">
          <div className="flex flex-wrap gap-5">
            {children}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default BracketAccordion;
